import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';


import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementSystemConfiguration, SystemConfigViewState } from '../../../bloc/BlocManagementSystemConfiguration';
import { SystemConfiguration, AppVersionDetail } from '../../../../types/cms.types';
import { CpnCreateEditSystemConfig } from './components/CpnCreateEditSystemConfig';

export const SystemConfigurationManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementSystemConfiguration());

    // UI state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);

    // Dialog states
    const [showCreateEditDialog, setShowCreateEditDialog] = useState<boolean>(false);
    const [selectedSystemConfig, setSelectedSystemConfig] = useState<SystemConfiguration | null>(null);

    // Refs
    const toast = useRef<Toast>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Search handler
    const handleSearch = async () => {
        await bloc.searchSystemConfigurations(searchTerm);
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear filters
    const clearFilters = async () => {
        setSearchTerm('');
        await bloc.clearFilters();
    };

    // Page change handler for DataTable's onPage event
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        // If rows changed, reset to page 1
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            // DataTable's page is 0-based, but our API expects 1-based
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refresh();
    };

    // Dialog handlers
    const handleCreateSystemConfig = () => {
        setSelectedSystemConfig(null);
        setShowCreateEditDialog(true);
    };

    const handleEditSystemConfig = (systemConfig: SystemConfiguration) => {
        setSelectedSystemConfig(systemConfig);
        setShowCreateEditDialog(true);
    };

    const handleCloseDialog = () => {
        setShowCreateEditDialog(false);
        setSelectedSystemConfig(null);
    };

    const handleSaveSystemConfig = async (systemConfigData: any) => {
        try {
            if (selectedSystemConfig) {
                await bloc.updateSystemConfiguration({
                    ...systemConfigData,
                    id: selectedSystemConfig.id
                });
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã cập nhật cấu hình hệ thống thành công',
                    life: 3000
                });
            } else {
                await bloc.createSystemConfiguration(systemConfigData);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã tạo cấu hình hệ thống thành công',
                    life: 3000
                });
            }
            handleCloseDialog();
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error.message || 'Không thể lưu cấu hình hệ thống',
                life: 3000
            });
        }
    };

    // Delete handler
    const handleDeleteSystemConfig = (systemConfig: SystemConfiguration) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa cấu hình hệ thống "${systemConfig.app_name}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.deleteSystemConfiguration(systemConfig.id!);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã xóa cấu hình hệ thống thành công',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || 'Không thể xóa cấu hình hệ thống',
                        life: 3000
                    });
                }
            }
        });
    };

    // Column templates
    const appInfoTemplate = (systemConfig: SystemConfiguration) => (
        <div>
            <div className="font-medium text-gray-900">{systemConfig.app_name}</div>
            {systemConfig.company_name && (
                <div className="text-sm text-gray-500">{systemConfig.company_name}</div>
            )}
        </div>
    );

    const versionDetailsTemplate = (systemConfig: SystemConfiguration) => {
        if (!systemConfig.version_details || systemConfig.version_details.length === 0) {
            return <span className="text-gray-400">Chưa có phiên bản</span>;
        }

        return (
            <div className="flex flex-wrap gap-1">
                {systemConfig.version_details.map((version: AppVersionDetail, index: number) => {
                    let severity: 'success' | 'info' | 'warning' | 'danger' = 'info';

                    if (version.maintenance_mode) {
                        severity = 'danger';
                    } else if (version.required_update_app) {
                        severity = 'warning';
                    }

                    return (
                        <div key={index} className="mb-1">
                            <Tag
                                value={`${version.os_type.toUpperCase()}: ${version.version}`}
                                severity={severity}
                                className="text-xs"
                            />
                            {(version.required_update_app || version.maintenance_mode) && (
                                <div className="text-xs text-gray-500 mt-1">
                                    {version.maintenance_mode && <span className="text-red-600">🔧 Bảo trì</span>}
                                    {version.required_update_app && <span className="text-orange-600"> ⬆️ Buộc nâng cấp</span>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const maintenanceTemplate = (systemConfig: SystemConfiguration) => {
        // Check if any version is in maintenance mode
        const hasMaintenanceVersion = systemConfig.version_details?.some(v => v.maintenance_mode) || false;
        const hasRequiredUpdate = systemConfig.version_details?.some(v => v.required_update_app) || false;

        return (
            <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasMaintenanceVersion ? 'bg-red-500' : 'bg-green-500'}`}></div>
                <div className="text-sm">
                    <div>{hasMaintenanceVersion ? 'Có phiên bản bảo trì' : 'Hoạt động'}</div>
                    {hasRequiredUpdate && (
                        <div className="text-orange-600 text-xs">⚠️ Yêu cầu nâng cấp</div>
                    )}
                </div>
            </div>
        );
    };

    const supportInfoTemplate = (systemConfig: SystemConfiguration) => (
        <div className="text-sm">
            {systemConfig.support_email && (
                <div className="text-gray-600 mb-1">
                    <i className="pi pi-envelope mr-1"></i>
                    {systemConfig.support_email}
                </div>
            )}
            {systemConfig.support_phone && (
                <div className="text-gray-600 mb-1">
                    <i className="pi pi-phone mr-1"></i>
                    {systemConfig.support_phone}
                </div>
            )}
            {systemConfig.support_fanpage && (
                <div className="text-gray-600 mb-1">
                    <i className="pi pi-facebook mr-1"></i>
                    <a href={systemConfig.support_fanpage} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        Fanpage
                    </a>
                </div>
            )}
            {systemConfig.support_zalo && (
                <div className="text-gray-600 mb-1">
                    <i className="pi pi-comments mr-1"></i>
                    <a href={systemConfig.support_zalo} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        Zalo
                    </a>
                </div>
            )}
            {systemConfig.support_group_facebook && (
                <div className="text-gray-600 mb-1">
                    <i className="pi pi-users mr-1"></i>
                    <a href={systemConfig.support_group_facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        Group FB
                    </a>
                </div>
            )}
        </div>
    );

    const dateTemplate = (systemConfig: SystemConfiguration) => (
        <div className="text-sm">
            <div className="text-gray-600">
                Tạo: {new Date(systemConfig.created_at!).toLocaleDateString('vi-VN')}
            </div>
            {systemConfig.updated_at !== systemConfig.created_at && (
                <div className="text-gray-500">
                    Cập nhật: {new Date(systemConfig.updated_at!).toLocaleDateString('vi-VN')}
                </div>
            )}
        </div>
    );

    const actionTemplate = (systemConfig: SystemConfiguration) => (
        <div className="flex gap-1">
            <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-warning p-button-sm"
                tooltip="Chỉnh sửa"
                onClick={() => handleEditSystemConfig(systemConfig)}
            />
            <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                tooltip="Xóa"
                onClick={() => handleDeleteSystemConfig(systemConfig)}
            />
        </div>
    );

    return (
        <AdminProtectedRoute
            requiredModule="system_management"
            requiredSubModule="system_configuration"
            requiredPermission="view">
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <div style={{ flex: 1, overflow: 'hidden', background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('info')}
                            initialData={{
                                systemConfigurations: [],
                                pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                                loading: false,
                                error: null,
                                filters: {},
                                hasData: false,
                                currentConfig: null
                            }}
                            builder={(snapshot: any) => {
                                const state: SystemConfigViewState = snapshot.data;

                                if (state.error) {
                                    return (
                                        <div style={{ padding: '24px' }}>
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                                    <div style={{ color: '#991b1b' }}>
                                                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                                        <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // Header template with filters and search
                                const headerTemplate = (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb',
                                        width: '100%'
                                    }}>
                                        {/* Search */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 300px', minWidth: '300px' }}>
                                            <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                            <InputText
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={handleSearchKeyPress}
                                                placeholder="Tìm kiếm cấu hình hệ thống..."
                                                style={{
                                                    flex: 1,
                                                    fontSize: '0.875rem',
                                                    padding: '8px 12px'
                                                }}
                                            />
                                            <Button
                                                icon="pi pi-search"
                                                onClick={handleSearch}
                                                className="p-button-outlined p-button-sm"
                                                style={{ padding: '6px 12px' }}
                                            />
                                        </div>

                                        {/* Action buttons */}
                                        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                            <Button
                                                label="Tạo mới"
                                                icon="pi pi-plus"
                                                onClick={handleCreateSystemConfig}
                                                className="p-button-success p-button-sm"
                                                style={{ padding: '6px 12px' }}
                                            />
                                            <Button
                                                icon="pi pi-filter-slash"
                                                onClick={clearFilters}
                                                className="p-button-outlined p-button-secondary p-button-sm"
                                                tooltip="Xóa bộ lọc"
                                                style={{ padding: '6px 12px' }}
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                onClick={handleRefresh}
                                                className="p-button-outlined p-button-sm"
                                                tooltip="Làm mới"
                                                style={{ padding: '6px 12px' }}
                                            />
                                        </div>
                                    </div>
                                );

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Current Configuration Info */}
                                        {state.currentConfig && (
                                            <div style={{
                                                padding: '16px 24px',
                                                background: '#f0f9ff',
                                                border: '1px solid #0ea5e9',
                                                borderRadius: '6px',
                                                margin: '16px 24px',
                                                flexShrink: 0
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <i className="pi pi-info-circle" style={{ color: '#0ea5e9' }}></i>
                                                    <span style={{ fontWeight: '500', color: '#0c4a6e' }}>
                                                        Cấu hình hiện tại: {state.currentConfig.app_name}
                                                    </span>
                                                    {state.currentConfig.version_details?.some(v => v.maintenance_mode) && (
                                                        <Tag value="Đang bảo trì" severity="danger" />
                                                    )}
                                                    {state.currentConfig.version_details?.some(v => v.required_update_app) && (
                                                        <Tag value="Yêu cầu nâng cấp" severity="warning" />
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight='calc(100vh - 250px)'
                                                value={state.systemConfigurations}
                                                loading={state.loading || lazyLoading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                className="system-config-management-table"
                                                emptyMessage="Không tìm thấy cấu hình hệ thống nào"
                                                responsiveLayout="scroll"
                                                scrollable
                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={state.pagination.total > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={state.pagination.total}
                                                first={(state.pagination.page - 1) * state.pagination.limit}
                                                onPage={onPageChange}
                                                rowsPerPageOptions={[10, 20, 50]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} bản ghi"
                                            >
                                                <Column
                                                    field="app_info"
                                                    header="Thông tin ứng dụng"
                                                    body={appInfoTemplate}
                                                    style={{ minWidth: '200px' }}
                                                    frozen
                                                />
                                                <Column
                                                    field="version_details"
                                                    header="Chi tiết phiên bản"
                                                    body={versionDetailsTemplate}
                                                    style={{ minWidth: '250px' }}
                                                />
                                                <Column
                                                    field="maintenance_mode"
                                                    header="Trạng thái"
                                                    body={maintenanceTemplate}
                                                    style={{ minWidth: '120px' }}
                                                />
                                                <Column
                                                    field="support_info"
                                                    header="Hỗ trợ"
                                                    body={supportInfoTemplate}
                                                    style={{ minWidth: '200px' }}
                                                />
                                                <Column
                                                    field="created_at"
                                                    header="Ngày tạo/cập nhật"
                                                    body={dateTemplate}
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Show message when no data and not loading */}
                                        {!state.loading && state.systemConfigurations.length === 0 && (
                                            <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                                <div style={{ color: '#6b7280' }}>
                                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '16px' }}></i>
                                                    <p>Chưa có cấu hình hệ thống nào</p>
                                                    <Button
                                                        label="Tạo cấu hình đầu tiên"
                                                        icon="pi pi-plus"
                                                        onClick={handleCreateSystemConfig}
                                                        className="p-button-outlined mt-3"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>
            </AdminLayout>

            {/* Create/Edit Dialog */}
            <CpnCreateEditSystemConfig
                visible={showCreateEditDialog}
                onHide={handleCloseDialog}
                systemConfig={selectedSystemConfig}
                onSave={handleSaveSystemConfig}
            />
        </AdminProtectedRoute>
    );
};